import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import DashboardChart from '../../../common/components/charts/DashboardChart';
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import {
	NETSALES,
	UNITS,
	DATA_NOT_AVAILABLE,
	ERROR_SCREEN_MSG_STORE,
} from '../../../constants/constant';
import RelativeCards, { useStyles } from './RelativeCards';
import { UserPortalContext } from '../../../context/appcontext';
import {
	getItemNetSales,
	getItemUnits,
	getItemRelativeNetSales,
	getItemRelativeUnits,
	getItemChannelBreakdown,
} from '../../../redux/actions/itemDrillDownActions';
import analytics from '../../../helper/analytics';
import tracker from '../../../constants/tracker';
import ChannelBreakdown from '../../../common/components/charts/ChannelBreakdown/ChannelBreakdown';

const Item = styled(Grid)(({ theme }) => ({
	...theme.typography.body2,
	padding: theme.spacing(1),
	textAlign: 'center',
	color: theme.palette.text.secondary,
}));

function Sales({
	weekFilter,
	category,
	selectedFilter,
	selectedTab,
	startDate,
	endDate,
	itemNum,
}) {
	const [width, setWidth] = useState(0);
	const classes = useStyles();
	const dispatch = useDispatch();
	const portalUserContext = React.useContext(UserPortalContext);
	const {
		netSalesData,
		netSalesLoading,
		netSalesErrorCode,
		unitsData,
		unitsLoading,
		unitsErrorCode,

		relativeNetSalesData,
		relativeNetSalesLoading,
		relativeNetSalesErrorCode,
		relativeUnitsData,
		relativeUnitsLoading,
		relativeUnitsErrorCode,

		channelBreakDownData,
		channelBreakDownloading,
		channelBreakDownErrorCode,
		unitsChannelBreakDownData,
		unitsChannelBreakDownLoading,
		unitsChannelBreakDownErrorCode,
	} = useSelector((state) => state.itemDrillDownReducer);

	const kpiName = useSelector((state) => state.salesReducer.kpiName);
	const selectedView = useSelector(
		(state) => state.appGuideReducer.selectedView
	);

	useEffect(() => {
		const updateSize = debounce(() => {
			setWidth(window.innerHeight);
		}, 1500);
		window.addEventListener('resize', updateSize);
		updateSize();
		const pageid = `${kpiName}Item${selectedTab}`
			.replaceAll(/\s/g, '')
			.toUpperCase();
		analytics.trackPageInfo(tracker.PAGE_ID[pageid]);
		return () => window.removeEventListener('resize', updateSize);
	}, []);

	React.useEffect(() => {
		if (itemNum) {
			const params = {
				categoryId: '-1',
				period: weekFilter,
				portalUserContext,
				itemNum,
				selectedFilter,
				startDate,
				endDate,
			};
			dispatch(getItemNetSales(params));
			dispatch(getItemUnits(params));
			dispatch(getItemRelativeNetSales(params));
			dispatch(getItemRelativeUnits(params));
			dispatch(getItemRelativeUnits(params));
			dispatch(getItemChannelBreakdown(params));
		}
	}, [weekFilter, category, itemNum, startDate, endDate, selectedView]);

	const cbData = {
		cbSalesData: channelBreakDownData,
		cbUnitsData: unitsChannelBreakDownData,
		isloading: [channelBreakDownloading, unitsChannelBreakDownLoading],
		isError: [channelBreakDownErrorCode, unitsChannelBreakDownErrorCode],
		summary:
			channelBreakDownData?.summary || unitsChannelBreakDownData?.summary,
	};

	return (
		<Box sx={{ flexGrow: 1 }}>
			<Grid container>
				<Grid
					data-test-id="net-sales-chart"
					item
					xs={12}
					sm={12}
					md={12}
					lg={4}
					xl={4}>
					<Item>
						<DashboardChart
							chartData={{
								data: netSalesData?.data,
								summary: netSalesData?.summary,
							}}
							chartTitle={NETSALES}
							chartName={'netsales'}
							weekFilter={weekFilter}
							selectedTab={selectedTab}
							isLoading={netSalesLoading}
							errorMsg={
								(netSalesErrorCode === 0 &&
									!netSalesData?.data &&
									netSalesData?.summary) ||
								netSalesErrorCode === 204
									? DATA_NOT_AVAILABLE
									: ERROR_SCREEN_MSG_STORE
							}
							updatedWidth={width}
							category={category}
							selectedFilter={selectedFilter}
							showExploreButton={false}
						/>
					</Item>
				</Grid>
				<Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
					<Item>
						<Box className={classes.container}>
							<RelativeCards
								name="Relative Net Sales"
								cardData={{
									data: relativeNetSalesData,
									loading: relativeNetSalesLoading,
									errorCode: relativeNetSalesErrorCode,
								}}
							/>
						</Box>
					</Item>
				</Grid>
				<Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
					<Item>
						<Box className={classes.container}>
							<ChannelBreakdown
								selectedFilter={selectedFilter}
								data={cbData}
								weekFilter={weekFilter}
								category={category}
								itemDrillDown={true}
							/>
						</Box>
					</Item>
				</Grid>
				<Grid item xs={12} sm={12} md={12} lg={4} xl={4}>
					<Item>
						<DashboardChart
							chartData={{
								data: unitsData?.data,
								summary: unitsData?.summary,
							}}
							chartTitle={UNITS}
							chartName={'salesvolume'}
							weekFilter={weekFilter}
							isLoading={unitsLoading}
							errorMsg={
								(unitsErrorCode === 0 &&
									!unitsData?.data &&
									unitsData?.summary) ||
								unitsErrorCode === 204
									? DATA_NOT_AVAILABLE
									: ERROR_SCREEN_MSG_STORE
							}
							updatedWidth={width}
							category={category}
							selectedFilter={selectedFilter}
							tab={selectedTab}
							selectedTab={selectedTab}
							showExploreButton={false}
						/>
					</Item>
				</Grid>
				<Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
					<Item>
						<Box data-test-id="relative-units" className={classes.container}>
							<RelativeCards
								name="Relative Units"
								cardData={{
									data: relativeUnitsData,
									loading: relativeUnitsLoading,
									errorCode: relativeUnitsErrorCode,
								}}
							/>
						</Box>
					</Item>
				</Grid>
			</Grid>
		</Box>
	);
}

Sales.propTypes = {
	selectedFilter: PropTypes.string,
	weekFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	category: PropTypes.string,
	selectedTab: PropTypes.string,
	startDate: PropTypes.string,
	endDate: PropTypes.string,
	itemNum: PropTypes.string,
};

export default Sales;
